/**
 * PdfExportPanel.jsx
 * ──────────────────────────────────────────────────────────────────────────────
 * Export PDF à l'échelle – rendu fidèle depuis la scène Three.js.
 *
 * Principe (identique à AutoCAD Paper Space) :
 *   • La scène Three.js (Model Space) est rendue dans un viewport orthographique
 *     calibré exactement sur le format papier + l'échelle choisie.
 *   • paperFactor = mmRéels_par_unitéDXF / dénominateurÉchelle
 *     Ex: unité=m, 1:1000 → 1000/1000 = 1 mm par mètre
 *   • La caméra offscreen voit exactement la zone monde qui tient sur la feuille.
 *   • Le zoom écran N'INTERVIENT JAMAIS dans ce calcul.
 *
 * Props :
 *   dxfData    – données brutes du parser (pour les calques et bbox de fallback)
 *   boundingBox – { min:[x,y], max:[x,y] }  (ou dxfData.dxf_extent)
 *   sceneRef   – React.RefObject<THREE.Scene>  (la scène vivante du DXFViewer2D)
 *   logoUrl    – URL optionnelle du logo cartouche
 *   onClose    – callback fermeture
 * ──────────────────────────────────────────────────────────────────────────────
 */

import React, {
  useState, useEffect, useRef, useMemo, useCallback,
} from "react";
import * as THREE from "three";

// ══════════════════════════════════════════════════════════════════════════════
//  CONSTANTES
// ══════════════════════════════════════════════════════════════════════════════

const PAPER_SIZES = {
  A4: { width: 210, height: 297, label: "A4  (210 × 297 mm)" },
  A3: { width: 297, height: 420, label: "A3  (297 × 420 mm)" },
};

const SCALES = [50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000];

const DXF_UNITS = {
  m:  { label: "Mètres (m)",       mmPerUnit: 1000 },
  cm: { label: "Centimètres (cm)", mmPerUnit: 10   },
  mm: { label: "Millimètres (mm)", mmPerUnit: 1    },
};

const TITLE_BLOCK_H = 18;   // mm

// Qualité d'export : label, DPI affiché, px/mm réels (1 pouce = 25.4 mm)
const QUALITY_PRESETS = [
  { key: "draft",  label: "Brouillon — 72 DPI",   dpi:  72, pxPerMm:  72 / 25.4 },
  { key: "screen", label: "Écran — 96 DPI",        dpi:  96, pxPerMm:  96 / 25.4 },
  { key: "medium", label: "Moyen — 150 DPI",       dpi: 150, pxPerMm: 150 / 25.4 },
  { key: "high",   label: "Haute — 254 DPI",       dpi: 254, pxPerMm: 254 / 25.4 },
  { key: "print",  label: "Impression — 300 DPI",  dpi: 300, pxPerMm: 300 / 25.4 },
];

// ══════════════════════════════════════════════════════════════════════════════
//  UTILITAIRES MATHÉMATIQUES (exportés pour tests unitaires)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * mm papier pour 1 unité DXF  =  mmRéels / échelle
 * Ex : m, 1:1000 → 1000/1000 = 1 mm/m
 */
export function calcPaperFactor(scaleDenom, dxfUnit = "m") {
  return DXF_UNITS[dxfUnit].mmPerUnit / scaleDenom;
}

/** Dimensions effectives (orientation prise en compte). */
export function effectivePaperDims(sizeKey, orientation) {
  const { width, height } = PAPER_SIZES[sizeKey];
  return orientation === "landscape"
    ? { width: height, height: width }
    : { width, height };
}

/** Normalise le bbox DXF quel que soit son format. */
export function normalizeBbox(bb) {
  if (!bb) return null;
  if (Array.isArray(bb.min) && Array.isArray(bb.max)) return bb;
  if (bb.minX !== undefined && bb.minY !== undefined)
    return { min: [bb.minX, bb.minY], max: [bb.maxX, bb.maxY] };
  if (Array.isArray(bb) && bb.length === 4)
    return { min: [bb[0], bb[1]], max: [bb[2], bb[3]] };
  return null;   // inconnu → le code utilisera le bbox THREE.js
}

/**
 * Calcule le bbox depuis les objets réels de la scène Three.js.
 * C'est la source de vérité : toujours correct, quel que soit le format DXF.
 * Retourne null si la scène est vide.
 */
function computeSceneBbox(scene) {
  if (!scene) return null;
  const box = new THREE.Box3().setFromObject(scene);
  if (box.isEmpty()) return null;
  return {
    min: [box.min.x, box.min.y],
    max: [box.max.x, box.max.y],
  };
}

// ══════════════════════════════════════════════════════════════════════════════
//  CAPTURE DE LA SCÈNE THREE.JS À L'ÉCHELLE PAPIER
//  C'est le cœur du système : équivalent du "viewport" AutoCAD.
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Calcule les paramètres de la caméra orthographique pour voir exactement
 * la zone monde correspondant à la zone imprimable à l'échelle choisie.
 *
 * @returns {{ camCx, camCy, worldW, worldH, printW, printH }}
 */
function calcCameraParams({
  bbox, paperFactor, paperDims, marginMm, offsetXmm, offsetYmm,
}) {
  const printW = paperDims.width  - 2 * marginMm;
  const printH = paperDims.height - 2 * marginMm - TITLE_BLOCK_H;

  // Zone monde visible = zone imprimable / facteur d'échelle
  const worldW = printW / paperFactor;
  const worldH = printH / paperFactor;

  // Centre bbox
  const bboxCx = (bbox.min[0] + bbox.max[0]) / 2;
  const bboxCy = (bbox.min[1] + bbox.max[1]) / 2;

  // Décalage utilisateur converti en unités monde
  // (offsetXmm > 0 → dessin se déplace à droite sur le papier → caméra regarde à gauche)
  const camCx = bboxCx - offsetXmm / paperFactor;
  // (offsetYmm > 0 → dessin se déplace en bas sur le papier → caméra regarde plus haut)
  const camCy = bboxCy + offsetYmm / paperFactor;

  return { camCx, camCy, worldW, worldH, printW, printH };
}

/**
 * Rend la scène Three.js dans un canvas offscreen à la résolution donnée.
 * Retourne la data-URL PNG ou null si la scène n'est pas disponible.
 *
 * @param {THREE.Scene}   scene
 * @param {object}        camParams  résultat de calcCameraParams
 * @param {number}        pxPerMm   pixels par mm (résolution)
 * @param {THREE.WebGLRenderer} renderer  renderer offscreen réutilisable
 * @returns {string|null}  data URL PNG
 */
function renderSceneToImage(scene, camParams, pxPerMm, renderer) {
  if (!scene || !renderer) return null;

  const { camCx, camCy, worldW, worldH, printW, printH } = camParams;
  const canvW = Math.max(1, Math.round(printW * pxPerMm));
  const canvH = Math.max(1, Math.round(printH * pxPerMm));

  renderer.setSize(canvW, canvH, false);

  const cam = new THREE.OrthographicCamera(
    camCx - worldW / 2,   // left
    camCx + worldW / 2,   // right
    camCy + worldH / 2,   // top
    camCy - worldH / 2,   // bottom
    -10000, 10000          // near/far large : objets à z=0 toujours visibles
  );
  cam.position.set(camCx, camCy, 500);
  cam.lookAt(camCx, camCy, 0);
  cam.updateProjectionMatrix();

  // Forcer fond blanc : la scène Three.js a un fond sombre (scene.background)
  // qui écrase le clearColor du renderer. On l'écarte temporairement.
  const savedBg = scene.background;
  scene.background = new THREE.Color(0xffffff);
  renderer.setClearColor(0xffffff, 1);

  renderer.render(scene, cam);

  scene.background = savedBg;   // restauration du fond original

  return renderer.domElement.toDataURL("image/png");
}

// ══════════════════════════════════════════════════════════════════════════════
//  CARTOUCHE (dessiné en canvas 2D sur toute la largeur du papier)
// ══════════════════════════════════════════════════════════════════════════════

function drawTitleBlock(ctx, ox, oy, pW, pH, tbHpx, meta, logoImg) {
  const tbY = oy + pH - tbHpx;

  ctx.fillStyle = "#f0f4f8";
  ctx.fillRect(ox, tbY, pW, tbHpx);

  ctx.strokeStyle = "#444";
  ctx.lineWidth   = 0.8;
  ctx.strokeRect(ox, tbY, pW, tbHpx);

  // Séparateurs verticaux : 16% | 50% | 76%
  const c1 = pW * 0.16, c2 = pW * 0.50, c3 = pW * 0.76;
  [c1, c2, c3].forEach(c => {
    ctx.beginPath();
    ctx.moveTo(ox + c, tbY);
    ctx.lineTo(ox + c, oy + pH);
    ctx.stroke();
  });

  const fB  = Math.max(8,  tbHpx * 0.22);
  const yM  = tbY + tbHpx * 0.55;
  const yT  = tbY + tbHpx * 0.32;
  const yBo = tbY + tbHpx * 0.70;

  // Zone 1 – Logo
  if (logoImg) {
    const lw = c1 * 0.70, lh = tbHpx * 0.60;
    ctx.drawImage(logoImg, ox + (c1 - lw) / 2, tbY + (tbHpx - lh) / 2, lw, lh);
  } else {
    ctx.fillStyle   = "#2d3a55";
    ctx.font        = `bold ${Math.round(fB * 1.1)}px sans-serif`;
    ctx.textAlign   = "center";
    ctx.fillText("◈ MADJEO", ox + c1 / 2, yM);
    ctx.textAlign   = "left";
  }

  // Zone 2 – Titre
  ctx.fillStyle = "#111";
  ctx.font      = `bold ${Math.round(fB * 1.05)}px sans-serif`;
  ctx.fillText(meta.title || "Sans titre", ox + c1 + 5, yM);

  // Zone 3 – Échelle
  ctx.font      = `bold ${Math.round(fB)}px sans-serif`;
  ctx.fillStyle = "#222";
  ctx.fillText(`Échelle : 1:${meta.scale}`, ox + c2 + 5, yT + 2);
  ctx.font      = `${Math.round(fB * 0.85)}px sans-serif`;
  ctx.fillText(`Unité   : ${meta.dxfUnit}`, ox + c2 + 5, yBo);

  // Zone 4 – Date / format
  const d = new Date().toLocaleDateString("fr-FR", { day:"2-digit", month:"2-digit", year:"numeric" });
  ctx.font = `bold ${Math.round(fB * 0.88)}px sans-serif`;
  ctx.fillText(`Date    : ${d}`, ox + c3 + 5, yT + 2);
  ctx.font = `${Math.round(fB * 0.80)}px sans-serif`;
  ctx.fillText(
    `${meta.paperSize}  ${meta.orientation === "landscape" ? "Paysage" : "Portrait"}`,
    ox + c3 + 5, yBo
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

/** Largeur de la fenêtre en temps réel. */
function useWindowWidth() {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return w;
}

export default function PdfExportPanel({
  dxfData, boundingBox, sceneRef, logoUrl, onClose,
}) {
  const winW = useWindowWidth();
  const isNarrow  = winW < 768;   // tablette/mobile : colonne unique
  const isTiny    = winW < 480;   // mobile : panel quasi plein écran

  // ── State ──────────────────────────────────────────────────────────────────
  const [paperSize,    setPaperSize]    = useState("A4");
  const [orientation,  setOrientation]  = useState("landscape");
  const [scale,        setScale]        = useState(1000);
  const [dxfUnit,      setDxfUnit]      = useState("m");
  const [marginMm,     setMarginMm]     = useState(10);
  const [offsetXmm,    setOffsetXmm]    = useState(0);
  const [offsetYmm,    setOffsetYmm]    = useState(0);
  const [title,        setTitle]        = useState("");
  const [showGrid,     setShowGrid]     = useState(true);
  const [qualityKey,   setQualityKey]   = useState("high");
  const [isGenerating, setIsGenerating] = useState(false);
  const [infoMsg,      setInfoMsg]      = useState(null); // { ok, text }

  const previewRef     = useRef(null);
  const previewWrapRef = useRef(null);
  const offRendererRef = useRef(null);   // THREE.WebGLRenderer offscreen (réutilisé)
  const logoImgRef     = useRef(null);
  const isDragging     = useRef(false);
  const dragStartPx    = useRef(null);
  const dragStartOff   = useRef(null);
  const renderPendRef  = useRef(false);  // évite les renders empilés
  const scheduleRenderRef = useRef(null); // référence stable vers scheduleRender

  // ── Données dérivées ───────────────────────────────────────────────────────
  // dxfBbox : bbox fournie par le backend (peut être null si format inconnu)
  const dxfBbox    = useMemo(() => normalizeBbox(boundingBox ?? dxfData?.dxf_extent), [boundingBox, dxfData]);
  const paperDims  = useMemo(() => effectivePaperDims(paperSize, orientation), [paperSize, orientation]);
  const paperFactor = useMemo(() => calcPaperFactor(scale, dxfUnit), [scale, dxfUnit]);

  const printableW = paperDims.width  - 2 * marginMm;
  const printableH = paperDims.height - 2 * marginMm - TITLE_BLOCK_H;

  // bbox effective : calculée depuis la scène au moment du render/calcul.
  // On l'expose comme helper inline (pas de useMemo : sceneRef.current est mutable).
  const getActiveBbox = useCallback(() =>
    computeSceneBbox(sceneRef?.current) ?? dxfBbox ?? { min: [0, 0], max: [1000, 1000] },
  [sceneRef, dxfBbox]);

  // Pour les affichages React synchrones (infoMsg, etc.)
  const bbox = getActiveBbox();

  const drawingWmm = (bbox.max[0] - bbox.min[0]) * paperFactor;
  const drawingHmm = (bbox.max[1] - bbox.min[1]) * paperFactor;

  const qualityPreset = QUALITY_PRESETS.find(q => q.key === qualityKey) ?? QUALITY_PRESETS[3];

  // ── Initialisation du renderer offscreen THREE.js ─────────────────────────
  useEffect(() => {
    const renderer = new THREE.WebGLRenderer({
      antialias: true, preserveDrawingBuffer: true, alpha: false,
    });
    renderer.setClearColor(0xffffff, 1);   // fond blanc pour le PDF
    renderer.setPixelRatio(1);
    offRendererRef.current = renderer;
    return () => {
      renderer.dispose();
      offRendererRef.current = null;
    };
  }, []);

  // ── Pré-chargement du logo ─────────────────────────────────────────────────
  useEffect(() => {
    if (!logoUrl) return;
    const img    = new Image();
    img.onload   = () => { logoImgRef.current = img; scheduleRender(); };
    img.onerror  = () => { logoImgRef.current = null; };
    img.src      = logoUrl;
  }, [logoUrl]); // eslint-disable-line

  // ══════════════════════════════════════════════════════════════════════════
  //  RENDU DE L'APERÇU
  // ══════════════════════════════════════════════════════════════════════════

  const renderPreview = useCallback(() => {
    const canvas = previewRef.current;
    if (!canvas) return;

    const ctx  = canvas.getContext("2d");
    const cW   = canvas.width;
    const cH   = canvas.height;

    ctx.clearRect(0, 0, cW, cH);

    // Fond gris (zone hors-papier)
    ctx.fillStyle = "#3c3c3c";
    ctx.fillRect(0, 0, cW, cH);

    // Facteur pixels/mm pour centrer la feuille dans le canvas de prévisualisation
    const PAD    = 20;
    const pxPerMmPrev = Math.min(
      (cW - PAD * 2) / paperDims.width,
      (cH - PAD * 2) / paperDims.height,
    );

    // Position du coin haut-gauche de la feuille dans le canvas de prévisualisation
    const pOffX  = (cW - paperDims.width  * pxPerMmPrev) / 2;
    const pOffY  = (cH - paperDims.height * pxPerMmPrev) / 2;
    const pW     = paperDims.width  * pxPerMmPrev;
    const pH     = paperDims.height * pxPerMmPrev;
    const mx     = marginMm * pxPerMmPrev;
    const tbHpx  = TITLE_BLOCK_H * pxPerMmPrev;

    // ── Ombre de la feuille ────────────────────────────────────────────────
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.5)";
    ctx.shadowBlur  = 14;
    ctx.shadowOffsetX = 5; ctx.shadowOffsetY = 5;
    ctx.fillStyle = "#fff";
    ctx.fillRect(pOffX, pOffY, pW, pH);
    ctx.restore();

    // ── Fond blanc ─────────────────────────────────────────────────────────
    ctx.fillStyle = "#fff";
    ctx.fillRect(pOffX, pOffY, pW, pH);

    // ── Grille de repère ───────────────────────────────────────────────────
    if (showGrid) {
      ctx.save();
      ctx.strokeStyle = "rgba(0,0,0,0.06)";
      ctx.lineWidth   = 0.5;
      for (let x = 0; x <= paperDims.width; x += 10) {
        ctx.beginPath();
        ctx.moveTo(pOffX + x * pxPerMmPrev, pOffY);
        ctx.lineTo(pOffX + x * pxPerMmPrev, pOffY + pH);
        ctx.stroke();
      }
      for (let y = 0; y <= paperDims.height; y += 10) {
        ctx.beginPath();
        ctx.moveTo(pOffX, pOffY + y * pxPerMmPrev);
        ctx.lineTo(pOffX + pW, pOffY + y * pxPerMmPrev);
        ctx.stroke();
      }
      ctx.restore();
    }

    // ── Bbox depuis la scène (source de vérité) ────────────────────────────
    const activeBbox = getActiveBbox();

    // ── Paramètres caméra ─────────────────────────────────────────────────
    const camParams = calcCameraParams({
      bbox: activeBbox, paperFactor, paperDims, marginMm, offsetXmm, offsetYmm,
    });

    // ── Dessin du contenu (Three.js scene → image) ─────────────────────────
    const scene = sceneRef?.current;

    function finishPreview(drawingImgEl) {
      // Clip à la zone imprimable (hors cartouche)
      ctx.save();
      ctx.beginPath();
      ctx.rect(pOffX + mx, pOffY + mx, pW - mx * 2, pH - mx * 2 - tbHpx);
      ctx.clip();

      if (drawingImgEl) {
        // L'image couvre exactement la zone imprimable (hors cartouche)
        ctx.drawImage(
          drawingImgEl,
          pOffX + mx, pOffY + mx,
          pW - mx * 2, pH - mx * 2 - tbHpx
        );
      } else {
        // Placeholder si aucune scène / donnée
        ctx.fillStyle   = "rgba(0,0,0,0.12)";
        ctx.font        = "12px sans-serif";
        ctx.textAlign   = "center";
        ctx.fillText("Chargez un fichier DXF ou dessinez dans le viewer",
          pOffX + pW / 2, pOffY + (pH - tbHpx) / 2);
        ctx.textAlign   = "left";
      }
      ctx.restore();

      // ── Marges (tirets) ────────────────────────────────────────────────
      ctx.save();
      ctx.strokeStyle = "#bbb";
      ctx.lineWidth   = 0.7;
      ctx.setLineDash([3, 3]);
      ctx.strokeRect(pOffX + mx, pOffY + mx, pW - mx * 2, pH - mx * 2 - tbHpx);
      ctx.setLineDash([]);
      ctx.restore();

      // ── Cartouche ──────────────────────────────────────────────────────
      drawTitleBlock(ctx, pOffX, pOffY, pW, pH, tbHpx,
        { title, scale, dxfUnit, paperSize, orientation }, logoImgRef.current);

      // ── Cadre de la feuille ────────────────────────────────────────────
      ctx.strokeStyle = "#666";
      ctx.lineWidth   = 1;
      ctx.strokeRect(pOffX, pOffY, pW, pH);

      // ── Info dimensionnelle ────────────────────────────────────────────
      const fits = drawingWmm <= printableW && drawingHmm <= printableH;
      setInfoMsg({
        ok: fits,
        text: fits
          ? `✓ Dessin ${drawingWmm.toFixed(0)} × ${drawingHmm.toFixed(0)} mm — tient dans la page`
          : `⚠ Dessin ${drawingWmm.toFixed(0)} × ${drawingHmm.toFixed(0)} mm > zone ${printableW.toFixed(0)} × ${printableH.toFixed(0)} mm`,
      });
    }

    if (scene && offRendererRef.current) {
      // Rendu Three.js à résolution preview (≈1-2 px/mm)
      const dataUrl = renderSceneToImage(
        scene, camParams, pxPerMmPrev, offRendererRef.current
      );
      if (dataUrl) {
        const img   = new Image();
        img.onload  = () => finishPreview(img);
        img.onerror = () => finishPreview(null);
        img.src     = dataUrl;
        return;  // finishPreview sera appelée dans onload
      }
    }
    // Fallback sync
    finishPreview(null);
  }, [
    paperDims, paperFactor, marginMm, offsetXmm, offsetYmm, getActiveBbox,
    showGrid, title, scale, dxfUnit, paperSize, orientation,
    printableW, printableH, drawingWmm, drawingHmm, sceneRef,
  ]);

  // Planifier un render sans empilement (résout le problème de l'img async)
  const scheduleRender = useCallback(() => {
    if (renderPendRef.current) return;
    renderPendRef.current = true;
    requestAnimationFrame(() => {
      renderPendRef.current = false;
      renderPreview();
    });
  }, [renderPreview]);

  // Synchronise le ref stable
  scheduleRenderRef.current = scheduleRender;

  // Re-render à chaque changement de paramètre
  useEffect(() => { scheduleRender(); }, [scheduleRender]);

  // ResizeObserver : adapte le canvas à son conteneur
  useEffect(() => {
    const container = previewWrapRef.current;
    const canvas    = previewRef.current;
    if (!container || !canvas) return;

    const ro = new ResizeObserver(() => {
      canvas.width  = container.clientWidth  || 600;
      canvas.height = container.clientHeight || 450;
      scheduleRender();
    });
    ro.observe(container);
    canvas.width  = container.clientWidth  || 600;
    canvas.height = container.clientHeight || 450;
    scheduleRender();
    return () => ro.disconnect();
  }, []); // eslint-disable-line

  // ── Drag-to-reposition dans l'aperçu ─────────────────────────────────────
  const onMouseDown = useCallback((e) => {
    isDragging.current   = true;
    dragStartPx.current  = { x: e.clientX, y: e.clientY };
    dragStartOff.current = { x: offsetXmm,  y: offsetYmm };
  }, [offsetXmm, offsetYmm]);

  const onMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    const canvas  = previewRef.current;
    if (!canvas)  return;
    const PAD     = 20;
    const pxPerMm = Math.min(
      (canvas.width  - PAD * 2) / paperDims.width,
      (canvas.height - PAD * 2) / paperDims.height,
    );
    setOffsetXmm(dragStartOff.current.x + (e.clientX - dragStartPx.current.x) / pxPerMm);
    setOffsetYmm(dragStartOff.current.y + (e.clientY - dragStartPx.current.y) / pxPerMm);
  }, [paperDims]);

  const onMouseUp = useCallback(() => { isDragging.current = false; }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup",   onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup",   onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  // Centrage automatique sur le contenu réel de la scène Three.js.
  // 1. Lit le vrai bbox via THREE.Box3 (tous les objets visibles).
  // 2. Calcule l'offset pour que le centre scène coïncide avec le centre papier.
  // 3. Ajuste l'échelle pour que tout le dessin tienne dans la page.
  const handleCenter = useCallback(() => {
    // Depuis que renderPreview utilise computeSceneBbox (source de vérité),
    // le centrage se fait en remettant les offsets à 0 et en choisissant
    // l'échelle qui fait tenir tout le dessin dans la page.
    const activeBbox = getActiveBbox();
    const sceneW = activeBbox.max[0] - activeBbox.min[0];
    const sceneH = activeBbox.max[1] - activeBbox.min[1];

    const PAD      = 0.90;
    const printW   = paperDims.width  - 2 * marginMm;
    const printH   = paperDims.height - 2 * marginMm - TITLE_BLOCK_H;
    const mmPerUnit = DXF_UNITS[dxfUnit].mmPerUnit;

    if (sceneW > 0 && sceneH > 0) {
      const neededW = (sceneW * mmPerUnit) / (printW * PAD);
      const neededH = (sceneH * mmPerUnit) / (printH * PAD);
      const needed  = Math.max(neededW, neededH);
      const best    = SCALES.find(s => s >= needed) ?? SCALES[SCALES.length - 1];
      setScale(best);
    }
    // offset = 0 : la caméra est déjà centrée sur le bbox de la scène
    setOffsetXmm(0);
    setOffsetYmm(0);
    setTimeout(() => scheduleRenderRef.current?.(), 80);
  }, [getActiveBbox, paperDims, marginMm, dxfUnit]);

  // ── Centrage automatique à chaque ouverture du panneau ──────────────────
  // Délai 200 ms : attend que la scène Three.js soit prête (renderer offscreen)
  useEffect(() => {
    const t = setTimeout(() => handleCenter(), 200);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ══════════════════════════════════════════════════════════════════════════
  //  GÉNÉRATION DU PDF
  // ══════════════════════════════════════════════════════════════════════════

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleGeneratePdf = async () => {
    setIsGenerating(true);
    try {
      const { jsPDF } = await import("jspdf");

      const pW  = paperDims.width;
      const pH  = paperDims.height;
      const mx  = marginMm;
      const tbH = TITLE_BLOCK_H;

      const pdf = new jsPDF({
        orientation: orientation === "landscape" ? "landscape" : "portrait",
        unit:        "mm",
        format:      paperSize.toLowerCase(),
      });

      // ── Fond blanc + cadre ─────────────────────────────────────────────
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pW, pH, "F");
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.5);
      pdf.rect(0, 0, pW, pH, "S");

      // ── Grille (optionnelle) ───────────────────────────────────────────
      if (showGrid) {
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.1);
        for (let x = 0; x <= pW; x += 10) pdf.line(x, 0, x, pH);
        for (let y = 0; y <= pH; y += 10) pdf.line(0, y, pW, y);
      }

      // ── Marges ────────────────────────────────────────────────────────
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.2);
      pdf.setLineDashPattern([1, 1], 0);
      pdf.rect(mx, mx, pW - 2 * mx, pH - 2 * mx - tbH, "S");
      pdf.setLineDashPattern([], 0);

      // ── Dessin THREE.js → PNG haute résolution → image dans le PDF ────
      const scene      = sceneRef?.current;
      const activeBbox = getActiveBbox();
      const camPar     = calcCameraParams({
        bbox: activeBbox, paperFactor, paperDims, marginMm, offsetXmm, offsetYmm,
      });

      if (scene && offRendererRef.current) {
        const dataUrl = renderSceneToImage(scene, camPar, qualityPreset.pxPerMm, offRendererRef.current);
        if (dataUrl) {
          // L'image couvre uniquement la zone imprimable (hors cartouche)
          pdf.addImage(
            dataUrl, "PNG",
            mx, mx,
            pW - 2 * mx,
            pH - 2 * mx - tbH,
            undefined, "FAST"
          );
        }
      }

      // ── Cartouche (canvas 2D → PNG → jsPDF) ────────────────────────
      // On re-dessine le cartouche sur un canvas dédié pour le PDF
      const tbCanvas  = document.createElement("canvas");
      const PX_MM     = qualityPreset.pxPerMm;
      tbCanvas.width  = Math.round(pW * PX_MM);
      tbCanvas.height = Math.round(tbH * PX_MM);
      const tbCtx     = tbCanvas.getContext("2d");
      drawTitleBlock(tbCtx, 0, 0, tbCanvas.width, tbCanvas.height, tbCanvas.height,
        { title, scale, dxfUnit, paperSize, orientation }, logoImgRef.current);
      pdf.addImage(
        tbCanvas.toDataURL("image/png"), "PNG",
        0, pH - tbH, pW, tbH,
        undefined, "FAST"
      );

      // ── Sauvegarde ────────────────────────────────────────────────────
      const fname = [title.trim().replace(/\s+/g, "_") || "plan", `1-${scale}`, paperSize, orientation].join("_") + ".pdf";
      pdf.save(fname);

    } catch (err) {
      console.error("[PdfExportPanel] Erreur:", err);
      alert("Erreur PDF : " + err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  //  JSX
  // ══════════════════════════════════════════════════════════════════════════

  const S = makeUI(isNarrow, isTiny);

  return (
    <div style={S.overlay}>
      <div style={S.panel}>

        {/* ── Entête ──────────────────────────────────────────────────── */}
        <div style={S.header}>
          <span style={{ fontWeight:"bold", fontSize:14 }}>
            Mise en page PDF — Export à l'échelle
          </span>
          <button onClick={onClose} style={S.closeBtn} title="Fermer">×</button>
        </div>

        <div style={S.body}>

          {/* ── Colonne paramètres ────────────────────────────────────── */}
          <div style={S.settings}>

            <Sect title="Format papier">
              <Row label="Format">
                <Sel value={paperSize} onChange={setPaperSize}>
                  {Object.entries(PAPER_SIZES).map(([k,v]) =>
                    <option key={k} value={k}>{v.label}</option>)}
                </Sel>
              </Row>
              <Row label="Orientation">
                <Sel value={orientation} onChange={setOrientation}>
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Paysage</option>
                </Sel>
              </Row>
              <Row label="Marges (mm)">
                <NumInput value={marginMm} onChange={setMarginMm} min={0} max={40} />
              </Row>
            </Sect>

            <Sect title="Échelle d'impression">
              <Row label="Échelle">
                <Sel value={scale} onChange={v => setScale(+v)}>
                  {SCALES.map(s => <option key={s} value={s}>1 : {s}</option>)}
                </Sel>
              </Row>
              <Row label="Unité DXF">
                <Sel value={dxfUnit} onChange={setDxfUnit}>
                  {Object.entries(DXF_UNITS).map(([k,v]) =>
                    <option key={k} value={k}>{v.label}</option>)}
                </Sel>
              </Row>

              {/* Indicateur de taille */}
              {infoMsg && (
                <div style={{
                  marginTop: 8, padding: "5px 8px", borderRadius: 4, fontSize: 11,
                  lineHeight: 1.4,
                  background: infoMsg.ok ? "rgba(0,160,80,.12)" : "rgba(220,100,0,.14)",
                  border:     `1px solid ${infoMsg.ok ? "rgba(0,160,80,.3)" : "rgba(220,100,0,.4)"}`,
                  color:      infoMsg.ok ? "#5eda90" : "#e0a050",
                }}>
                  {infoMsg.text}
                </div>
              )}
            </Sect>

            <Sect title="Positionnement du dessin">
              <Row label="Décalage X (mm)">
                <NumInput value={offsetXmm.toFixed(1)} onChange={v => setOffsetXmm(+v)} step={0.5} />
              </Row>
              <Row label="Décalage Y (mm)">
                <NumInput value={offsetYmm.toFixed(1)} onChange={v => setOffsetYmm(+v)} step={0.5} />
              </Row>
              <button onClick={handleCenter} style={S.centerBtn}>
                ⊕ Centrer automatiquement
              </button>
            </Sect>

            <Sect title="Qualité d'export">
              <Row label="Résolution PDF">
                <Sel value={qualityKey} onChange={setQualityKey}>
                  {QUALITY_PRESETS.map(q => (
                    <option key={q.key} value={q.key}>{q.label}</option>
                  ))}
                </Sel>
              </Row>
              <div style={{ fontSize:10, color:"#666", marginTop:4, lineHeight:1.5 }}>
                {qualityKey === "draft"  && "Fichier léger, traits grossiers — aperçu rapide"}
                {qualityKey === "screen" && "Suffisant pour écran, fichier compact"}
                {qualityKey === "medium" && "Bon compromis qualité / taille de fichier"}
                {qualityKey === "high"   && "Recommandé pour l'impression courante"}
                {qualityKey === "print"  && "Qualité maximale — fichier volumineux"}
              </div>
            </Sect>

            <Sect title="Cartouche">
              <Row label="Titre du plan">
                <input
                  type="text" value={title} placeholder="Ex : Plan topographique…"
                  onChange={e => setTitle(e.target.value)}
                  style={{ ...S.input, width:"100%", boxSizing:"border-box" }}
                />
              </Row>
              <Row label="Grille de repère">
                <input type="checkbox" checked={showGrid}
                  onChange={e => setShowGrid(e.target.checked)}
                  style={{ cursor:"pointer" }} />
              </Row>
            </Sect>

            <button
              onClick={handleGeneratePdf}
              disabled={isGenerating}
              style={S.genBtn(isGenerating)}
            >
              {isGenerating ? "Génération…" : "⬇  Générer le PDF"}
            </button>

          </div>

          {/* ── Aperçu ───────────────────────────────────────────────── */}
          <div style={S.previewArea}>
            <div style={S.previewHint}>
              Glissez le dessin pour le repositionner · Modifiez l'échelle à gauche
            </div>
            <div
              ref={previewWrapRef}
              style={S.previewWrap}
              onMouseDown={onMouseDown}
            >
              <canvas
                ref={previewRef}
                style={{ display:"block", width:"100%", height:"100%", cursor:"grab" }}
              />
            </div>
            <div style={S.previewFooter}>
              <span>1 : {scale}</span>
              <span>{paperSize}  {orientation === "landscape" ? "Paysage" : "Portrait"}</span>
              <span style={{ opacity:.5 }}>PDF : {qualityPreset.dpi} DPI</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  MINI-COMPOSANTS UI
// ══════════════════════════════════════════════════════════════════════════════

function Sect({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        fontSize: 10, fontWeight:"bold", color:"#7aa6d4",
        textTransform:"uppercase", letterSpacing:".12em",
        borderBottom:"1px solid #2d3a55", paddingBottom: 4, marginBottom: 8,
      }}>{title}</div>
      {children}
    </div>
  );
}
function Row({ label, children }) {
  return (
    <label style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
      marginBottom: 7, gap: 8 }}>
      <span style={{ color:"#aaa", fontSize:12, flexShrink:0, width:120 }}>{label}</span>
      <div style={{ flex:1 }}>{children}</div>
    </label>
  );
}
function Sel({ value, onChange, children }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      width:"100%", background:"#2d3a55", color:"#ddd",
      border:"1px solid #4a5a80", borderRadius:4, padding:"4px 6px", fontSize:12, cursor:"pointer",
    }}>{children}</select>
  );
}
function NumInput({ value, onChange, min, max, step = 1 }) {
  return (
    <input type="number" min={min} max={max} step={step} value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        width:70, background:"#2d3a55", color:"#ddd",
        border:"1px solid #4a5a80", borderRadius:4, padding:"3px 6px", fontSize:12,
      }} />
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  STYLES (fonction pour supporter le responsive)
// ══════════════════════════════════════════════════════════════════════════════

function makeUI(isNarrow, isTiny) { return {
  overlay: {
    position:"fixed", inset:0, background:"rgba(0,0,0,.65)",
    zIndex:9500, display:"flex", alignItems:"center", justifyContent:"center",
    backdropFilter:"blur(4px)",
  },
  panel: {
    background:"#1a2236", border:"1px solid #2d3a55",
    borderRadius: isTiny ? 0 : 10,
    boxShadow:"0 16px 48px rgba(0,0,0,.7)",
    width:  isTiny ? "100vw"  : "min(97vw,1200px)",
    height: isTiny ? "100dvh" : "min(93vh,800px)",
    display:"flex", flexDirection:"column",
    overflow: isNarrow ? "auto" : "hidden",
    fontFamily:"Segoe UI,sans-serif", color:"#ccc",
  },
  header: {
    display:"flex", justifyContent:"space-between", alignItems:"center",
    padding:"11px 18px", background:"#0f1729", borderBottom:"1px solid #2d3a55", flexShrink:0,
  },
  closeBtn: {
    background:"transparent", border:"none", color:"#aaa",
    fontSize:22, cursor:"pointer", lineHeight:1, padding:"0 4px",
  },
  body: {
    display:"flex",
    flexDirection: isNarrow ? "column" : "row",
    flex: isNarrow ? "none" : 1,
    overflow: isNarrow ? "visible" : "hidden",
  },
  settings: {
    width: isNarrow ? "100%" : 295,
    flexShrink:0, padding:"14px 14px",
    overflowY: isNarrow ? "visible" : "auto",
    borderRight:   isNarrow ? "none"               : "1px solid #2d3a55",
    borderBottom:  isNarrow ? "1px solid #2d3a55"  : "none",
    boxSizing:"border-box",
  },
  previewArea: {
    flex:1, display:"flex", flexDirection:"column",
    background:"#131929", padding:12,
    overflow: isNarrow ? "visible" : "hidden",
    gap:6,
    minHeight: isNarrow ? 320 : undefined,
  },
  previewHint: {
    fontSize:11, color:"#555", flexShrink:0,
  },
  previewWrap: {
    flex:1, overflow:"hidden", borderRadius:4, background:"#3c3c3c",
    minHeight: isNarrow ? 280 : 200, cursor:"grab",
  },
  previewFooter: {
    display:"flex", justifyContent:"space-between",
    fontSize:11, color:"#555", flexShrink:0,
    flexWrap:"wrap", gap:4,
  },
  input: {
    background:"#2d3a55", color:"#ddd",
    border:"1px solid #4a5a80", borderRadius:4, padding:"3px 7px", fontSize:12,
  },
  centerBtn: {
    width:"100%", padding:"7px 0", marginTop:4,
    background:"#1e3a5f", border:"1px solid #4a9eff", color:"#4a9eff",
    borderRadius:4, cursor:"pointer", fontSize:12,
  },
  genBtn: (disabled) => ({
    width:"100%", padding:"10px 0", marginTop:14,
    background: disabled ? "#222" : "linear-gradient(135deg,#1a6b3a,#2d9e55)",
    border:`1px solid ${disabled ? "#444" : "#2d9e55"}`,
    color: disabled ? "#555" : "#fff",
    borderRadius:6, cursor: disabled ? "default" : "pointer",
    fontSize:14, fontWeight:"bold", letterSpacing:".05em",
  }),
}; }
